import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';

const API_URL = "https://heou1vaond.execute-api.us-west-1.amazonaws.com/Prod"
const path = "/invoice"

const apiHeaders = {
    "Content-Type": 'application/json',
    "x-api-key": "nMEyHpJKWa8ks2jCvOkJ6akQcQh6Oloz6G3FW7yZ"
}

// POST /invoice routes on `action`, so one endpoint can handle every invoice
// operation. Keep these in sync with the switch in the Lambda.
const INVOICE_ACTIONS = {
    MARK_COMPLETED: 'mark_completed',
    CREATE_CHECKOUT: 'create_checkout',
};

// The Lambda's JSON body arrives wrapped ({ statusCode, body: "..." }) under
// non-proxy integration, or bare under proxy integration.
const unwrapBody = (data) =>
    typeof data?.body === 'string' ? parseJSON(data.body, {}) : (data || {});

// Paid as far as the database is concerned (e.g. after Stripe's webhook).
const rowIsPaid = (row) =>
    !!row && (row.status === 'paid' || !!row.paid_at ||
        (row.amount_owed != null && Number(row.amount_owed) <= 0 && Number(row.total) > 0));

// Height of the fixed nav. The branding swap fires when the avatars in the
// page header pass underneath this line (same as OfferingsPage).
const NAV_HEIGHT = 74;

// Keys match the values stored in vw_invoices.payment_methods.accepted.
const PAYMENT_METHODS = {
    card:     { label: 'Credit or debit card', icon: 'fas fa-credit-card' },
    zelle:    { label: 'Zelle',                icon: 'fas fa-building-columns' },
    venmo:    { label: 'Venmo',                icon: 'fab fa-vimeo-v' },
    cashapp:  { label: 'Cash App',             icon: 'fas fa-dollar-sign' },
    paypal:   { label: 'PayPal',               icon: 'fab fa-paypal' },
    inperson: { label: 'In Person',            icon: 'fas fa-handshake', sub: 'Pay the business directly.' },
};

// ── Per-method payment details (mirrors PurchaseConfirmationMobile) ──

// Zelle and in-person surface a phone number; Venmo an @handle; Cash App a
// $Cashtag; PayPal an email address.
const RECIPIENT_NOUN = {
    zelle: 'number',
    venmo: 'address',
    cashapp: 'Cashtag',
    paypal: 'email address',
};

// Only the deep-link methods get a brand-colored CTA.
const PAY_ACCENT_CLASS = { venmo: 'venmo', cashapp: 'cashapp' };

const formatMobileNumber = (number) => {
    const cleaned = ('' + number).replace(/\D/g, '');
    if (cleaned.length === 10) return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    if (cleaned.length === 11 && cleaned.startsWith('1')) return cleaned.replace(/1(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3');
    if (cleaned.length > 11) return '+' + cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4}).*/, '$1 $2 $3 $4');
    return number;
};

/**
 * Venmo: https://venmo.com/<handle>?txn=pay&amount=<amount>
 *   txn=pay makes the person opening the link the payer; audience=private
 *   keeps it off the public feed.
 * Cash App: https://cash.app/$<cashtag>/<amount>
 *   Amount is a path segment; dropped at $0 so the link still opens the profile.
 * PayPal has no equivalent (paypal_id is an email), so it's copy-only.
 */
const buildPayUrl = (methodKey, handle, amount) => {
    if (!handle) return null;
    if (methodKey === 'venmo') {
        return (
            `https://venmo.com/${encodeURIComponent(handle)}` +
            `?txn=pay&amount=${amount.toFixed(2)}` +
            `&note=${encodeURIComponent('Verca invoice')}` +
            `&audience=private`
        );
    }
    if (methodKey === 'cashapp') {
        const base = `https://cash.app/$${encodeURIComponent(handle)}`;
        return amount > 0 ? `${base}/${amount.toFixed(2)}` : base;
    }
    return null;
};

// Some columns (line_items, payment_methods) arrive as JSON strings.
const parseJSON = (value, fallback) => {
    if (value == null) return fallback;
    if (typeof value !== 'string') return value;
    try { return JSON.parse(value); } catch (e) { return fallback; }
};

// The Lambda returns { statusCode, body: "{\"invoice\":[row]}" }. Unwrap the
// body, then take the first row of the `invoice` array.
const extractInvoice = (data) => {
    if (!data) return null;
    let d = data;
    if (typeof d === 'string') d = parseJSON(d, {});
    if (typeof d?.body === 'string') d = parseJSON(d.body, {});
    const rows = d?.invoice ?? d;
    const row = Array.isArray(rows) ? rows[0] : rows;
    // Only accept something that actually looks like an invoice row, so error
    // payloads ({ message }, { errorType }, { error }) show "Not Found"
    // instead of rendering garbage.
    return row && typeof row === 'object' && row.invoice_id ? row : null;
};

const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const money = (v) => `$${toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Postgres timestamps look like "2026-10-10 00:02:02.340793+00", which Safari
// can't parse. Normalize to ISO 8601: "T" separator, ms precision, "+00:00".
const parseTimestamp = (value) => {
    if (!value) return null;
    const iso = String(value)
        .trim()
        .replace(' ', 'T')
        .replace(/(\.\d{3})\d+/, '$1')
        .replace(/([+-]\d{2})$/, '$1:00');
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (value) => {
    const d = parseTimestamp(value);
    return d ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;
};

const initialsOf = (name) =>
    (name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(w => w[0]?.toUpperCase() || '')
        .join('');

// Circular avatar that falls back to initials (or an icon) when the URL is
// missing or fails to load.
const Avatar = ({ src, name, fallbackIcon, className = '' }) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [src]);
    const initials = initialsOf(name);
    return (
        <div className={`avatar ${className}`} title={name || undefined}>
            {src && !failed ? (
                <img src={src} alt={name || ''} onError={() => setFailed(true)} />
            ) : initials ? (
                <span className="avatar-initials">{initials}</span>
            ) : (
                <i className={fallbackIcon}></i>
            )}
        </div>
    );
};

const STATUS_LABEL = {
    pending: 'Unpaid',
    paid: 'Paid',
    cancelled: 'Cancelled',
    partial: 'Partially paid',
    awaiting: 'Marked as paid',
};

const InvoicesPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    // Stripe sends the customer back to ?checkout=success after paying.
    const [searchParams] = useSearchParams();
    const returnedFromCheckout = searchParams.get('checkout') === 'success';
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [copied, setCopied] = useState(false);

    // Nav branding.
    // Once the avatars at the top of the invoice scroll up behind the nav, the
    // "Verca" wordmark hands off to a small version of them plus the business
    // name, so you always know who the invoice is from.
    const brandRef = useRef(null);
    const [navBranded, setNavBranded] = useState(false);

    // "Mark as Completed" submission state.
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    // The method the client marked it paid with, for the confirmation banner.
    // Only known for this visit; the view doesn't store it.
    const [markedMethod, setMarkedMethod] = useState(null);

    // Card checkout: once a Stripe session is created, the page switches to
    // a "checkout should have opened" screen with this URL as the fallback link.
    const [checkoutUrl, setCheckoutUrl] = useState(null);

    useEffect(() => {
        setLoading(true);
        setInvoice(null);
        setPaymentMethod(null);
        setNavBranded(false);
        setSubmitting(false);
        setSubmitError('');
        setMarkedMethod(null);
        setCheckoutUrl(null);

        async function fetchInvoiceAPI() {
            try {
                const resp = await axios.get(`${API_URL}${path}`, {
                    params: { invoiceId: id },
                    headers: apiHeaders
                });
                console.log("Invoices response:", resp.data);
                console.log(JSON.stringify(resp.data));

                if (resp.data?.statusCode && resp.data.statusCode !== 200) {
                    console.log("Invoice fetch failed:", resp.data.body);
                    return;
                }
                setInvoice(extractInvoice(resp.data));
            } catch (e) {
                console.log("Error fetching invoice", e);
            } finally {
                setLoading(false);
            }
        }

        fetchInvoiceAPI();
    }, [id]);

    // ── Nav branding hand-off ──
    // Watch the avatar row itself rather than a magic scroll offset, so the swap
    // lands exactly as the real branding disappears behind the nav.
    useEffect(() => {
        if (loading || !invoice) {
            setNavBranded(false);
            return;
        }
        const el = brandRef.current;
        if (!el) return;

        if (typeof IntersectionObserver === 'undefined') {
            // Older browsers: fall back to a plain scroll threshold.
            const onScroll = () => setNavBranded(window.scrollY > 140);
            onScroll();
            window.addEventListener('scroll', onScroll, { passive: true });
            return () => window.removeEventListener('scroll', onScroll);
        }

        const io = new IntersectionObserver(
            ([entry]) => setNavBranded(!entry.isIntersecting),
            { root: null, rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`, threshold: 0 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [loading, invoice]);

    // While the customer is off in the Stripe tab, re-check the invoice each
    // time they come back to this one, so it flips to "Paid" by itself once
    // the payment goes through.
    useEffect(() => {
        if (!checkoutUrl) return;

        const recheck = async () => {
            if (document.visibilityState === 'hidden') return;
            try {
                const resp = await axios.get(`${API_URL}${path}`, {
                    params: { invoiceId: id },
                    headers: apiHeaders
                });
                const row = extractInvoice(resp.data);
                if (rowIsPaid(row)) {
                    setInvoice(row);
                    setCheckoutUrl(null);
                    setPaymentMethod(null);
                }
            } catch (e) {
                console.log("Error re-checking invoice", e);
            }
        };

        window.addEventListener('focus', recheck);
        document.addEventListener('visibilitychange', recheck);
        return () => {
            window.removeEventListener('focus', recheck);
            document.removeEventListener('visibilitychange', recheck);
        };
    }, [checkoutUrl, id]);

    // The nav branding doubles as the way back up to the full header.
    const scrollToTop = () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    };

    // ── Derived invoice fields (vw_invoices columns) ──
    const businessName = invoice?.server_name;
    const serverAvatar = invoice?.server_avatar_url;
    const clientAvatar = invoice?.client_avatar_url;
    const customerName = invoice?.client_name;
    // Not in the view yet; the Customer card shows them if they're added.
    const customerEmail = invoice?.client_email;
    const customerPhone = invoice?.client_phone;

    const sentDate = fmtDate(invoice?.sent_at || invoice?.created_at);
    const paidDate = fmtDate(invoice?.paid_at);
    const cancelledDate = fmtDate(invoice?.cancelled_at);

    const total = toNum(invoice?.total);
    const amountOwed = invoice?.amount_owed != null ? toNum(invoice.amount_owed) : total;
    const amountPaid = Math.max(0, total - amountOwed);

    const isCancelled = !!invoice?.cancelled_at || invoice?.status === 'cancelled';
    const isPaid = !isCancelled && (invoice?.status === 'paid' || !!invoice?.paid_at || (total > 0 && amountOwed <= 0));
    // Client already hit "Mark as Completed"; waiting on the business to confirm.
    const isAwaitingConfirmation = !isCancelled && !isPaid && invoice?.marked_as_completed === true;
    const statusKey = isCancelled
        ? 'cancelled'
        : isPaid
            ? 'paid'
            : isAwaitingConfirmation
                ? 'awaiting'
                : amountPaid > 0 ? 'partial' : (invoice?.status || 'pending');

    const rawItems = parseJSON(invoice?.line_items, []);
    const lineItems = (Array.isArray(rawItems) ? rawItems : [])
        .filter(it => it && typeof it === 'object')
        .sort((a, b) => toNum(a.sort_order) - toNum(b.sort_order))
        .map((it, i) => {
            const qty = toNum(it.quantity) || 1;
            const unit = toNum(it.unitPrice);
            const lineTotal = unit ? unit * qty : toNum(it.bundlePrice);
            return {
                key: it.productuid || i,
                name: it.name || `Item ${i + 1}`,
                image: it.image,
                note: it.note,
                qty,
                unit,
                lineTotal,
            };
        });
    const subtotal = lineItems.reduce((sum, it) => sum + it.lineTotal, 0);
    // Anything on the invoice total beyond the line items (tax, fees).
    const adjustments = Math.round((total - subtotal) * 100) / 100;

    // Where to send money for each method, from the business's account.
    const recipientFor = (key) => String({
        zelle: invoice?.numberzelle,
        venmo: invoice?.venmoid,
        cashapp: invoice?.cashapp_id,
        paypal: invoice?.paypal_id,
    }[key] ?? '').trim();

    // A method is only offered if the business can actually receive it: card
    // needs a Stripe account, the handle-based methods need their handle, and
    // in person needs nothing.
    const isMethodReady = (key) => {
        if (key === 'inperson') return true;
        if (key === 'card') return !!invoice?.STRIPE_acctID;
        return !!recipientFor(key);
    };

    // Accepted methods the business has set up, preferred ones first.
    // payment_methods arrives as an object (older rows: a JSON string).
    const pm = parseJSON(invoice?.payment_methods, {});
    const preferred = Array.isArray(pm?.preferred) ? pm.preferred : [];
    const accepted = (Array.isArray(pm?.accepted) ? pm.accepted : [])
        .filter(k => PAYMENT_METHODS[k] && isMethodReady(k))
        .sort((a, b) => (preferred.includes(b) ? 1 : 0) - (preferred.includes(a) ? 1 : 0));

    const handleCopy = (value) => {
        try {
            navigator.clipboard.writeText(String(value));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.log("Copy failed", e);
        }
    };

    const selectMethod = (key) => {
        setPaymentMethod(key);
        setCopied(false);
        setSubmitError('');
    };

    // Expanded panel under the selected card: deep-link button (Venmo,
    // Cash App), then the copyable recipient; in-person gets instructions.
    const renderMethodDetails = (key) => {
        if (key === 'card') return null;

        if (key === 'inperson') {
            return (
                <div className="method-detail">
                    <p className="detail-message">
                        Bring <strong>{money(amountOwed)}</strong> in cash or check to your
                        appointment. You'll pay your provider directly when you arrive.
                    </p>
                </div>
            );
        }

        const raw = recipientFor(key);
        const isHandle = key === 'venmo' || key === 'cashapp';
        const handle = isHandle ? raw.replace(/^[@$]+/, '') : '';
        const display = key === 'zelle'
            ? formatMobileNumber(raw)
            : isHandle ? `${key === 'cashapp' ? '$' : '@'}${handle}` : raw;
        // Venmo/Cash App copy the bare handle (what pastes into their search).
        const copyValue = isHandle ? handle : raw;
        const payUrl = buildPayUrl(key, handle, amountOwed);
        const label = PAYMENT_METHODS[key].label;
        const noun = RECIPIENT_NOUN[key] || 'account';

        if (!raw) {
            return (
                <div className="method-detail">
                    <p className="detail-message">
                        Send <strong>{money(amountOwed)}</strong> via {label}. Contact the
                        business for their {label} {noun}.
                    </p>
                </div>
            );
        }

        return (
            <div className="method-detail">
                {/* The pay button already states the amount, so the "Send $X"
                    line only shows for copy-only methods (Zelle, PayPal). */}
                {!payUrl && (
                    <p className="detail-message">
                        Send <strong>{money(amountOwed)}</strong> to the {noun} below via {label}:
                    </p>
                )}

                {payUrl && (
                    <>
                        <a
                            href={payUrl}
                            className={`pay-link ${PAY_ACCENT_CLASS[key]}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span>Pay {money(amountOwed)} with {label}</span>
                        </a>
                        <div className="or-divider"><span>or</span></div>
                        <p className="address-label">Send payment to this {noun}</p>
                    </>
                )}

                <button type="button" className="copy-field" onClick={() => handleCopy(copyValue)}>
                    <span className="copy-text">{display}</span>
                    <span className="copy-action">
                        <i className={copied ? 'fas fa-check' : 'fas fa-copy'}></i>
                        <span>{copied ? 'Copied!' : 'Copy'}</span>
                    </span>
                </button>
            </div>
        );
    };

    // Button state by selected method:
    //   card     → black "Pay"
    //   inperson → grayed out "Pay in person" (nothing to do online)
    //   others   → black "Mark as Completed"
    const payDisabled = !paymentMethod || paymentMethod === 'inperson';
    const payLabel = !paymentMethod
        ? 'Select a payment method'
        : paymentMethod === 'card'
            ? 'Pay'
            : paymentMethod === 'inperson'
                ? 'Pay in person'
                : 'Mark as Completed';

    // Card: the Pay button shows a spinner and "Opening secure checkout"
    // while the Lambda creates the Stripe session. Once the URL comes back,
    // this tab goes to Stripe, the same way ServicesPage does it. A same-tab
    // redirect is never popup-blocked, so it works on mobile Safari too.
    // Stripe's success/cancel URLs bring the customer back to this invoice.
    // The "A checkout window should open" panel shows for the moment before
    // the redirect, and its button is the fallback if it doesn't happen.
    const startCardCheckout = async () => {
        setSubmitting(true);
        setSubmitError('');
        try {
            const resp = await axios.post(`${API_URL}${path}`, {
                action: INVOICE_ACTIONS.CREATE_CHECKOUT,
                invoiceId: invoice.invoice_id,
            }, { headers: apiHeaders });
            console.log("Create checkout response:", resp.data);

            const body = unwrapBody(resp.data);
            if ((resp.data?.statusCode && resp.data.statusCode !== 200) || !body?.url) {
                const err = new Error(body?.error || 'No checkout URL returned');
                // The Lambda's error strings are written for customers
                // ("Card payments are not accepted for this invoice").
                err.userMessage = body?.error;
                throw err;
            }

            setCheckoutUrl(body.url);
            setSubmitting(false);
            window.location.href = body.url;
        } catch (e) {
            // Under proxy integration a 4xx/5xx arrives as an axios error with
            // the body on e.response. No e.response at all means the browser
            // never got a readable response (network or CORS).
            const reason = e.userMessage || unwrapBody(e.response?.data)?.error;
            console.log("Error creating checkout:", reason || e.message, {
                status: e.response?.status,
                response: e.response?.data,
                error: e,
            });
            setSubmitError(reason
                ? `We couldn't start checkout: ${reason}`
                : "We couldn't start checkout. Please try again.");
            setSubmitting(false);
        }
    };

    const handlePay = async () => {
        if (payDisabled || submitting) return;

        if (paymentMethod === 'card') {
            startCardCheckout();
            return;
        }

        // Zelle, Venmo, Cash App, PayPal: the client paid outside Verca and is
        // telling the business so.
        setSubmitting(true);
        setSubmitError('');
        try {
            const resp = await axios.post(`${API_URL}${path}`, {
                action: INVOICE_ACTIONS.MARK_COMPLETED,
                invoiceId: invoice.invoice_id,
                serveruid: invoice.serveruid || null,
                clientName: customerName || null,
                paymentMethod,
                amount: amountOwed,
            }, { headers: apiHeaders });
            console.log("Mark as paid response:", resp.data);

            // Non-proxy integrations wrap the Lambda result, so a 200 HTTP
            // response can still carry a failed statusCode.
            if (resp.data?.statusCode && resp.data.statusCode !== 200) {
                throw new Error(typeof resp.data.body === 'string' ? resp.data.body : 'Request failed');
            }

            showMarkedAsPaid(paymentMethod);
        } catch (e) {
            console.log("Error marking invoice as paid", e);

            // The request can fail on the browser side (e.g. a blocked
            // response) even though the invoice was marked. Check the server
            // before showing an error.
            if (await isMarkedOnServer()) {
                showMarkedAsPaid(paymentMethod);
                return;
            }

            setSubmitError("We couldn't mark this invoice as paid. Please try again.");
            setSubmitting(false);
        }
    };

    // Switch the page to the "Marked as paid" state in place: flipping the
    // local copy hides the payment options and shows the confirmation banner.
    const showMarkedAsPaid = (method) => {
        setMarkedMethod(method);
        setPaymentMethod(null);
        setSubmitting(false);
        setInvoice(prev => ({ ...prev, marked_as_completed: true }));
    };

    // Re-fetches the invoice and reports whether it's already marked.
    const isMarkedOnServer = async () => {
        try {
            const resp = await axios.get(`${API_URL}${path}`, {
                params: { invoiceId: id },
                headers: apiHeaders
            });
            return extractInvoice(resp.data)?.marked_as_completed === true;
        } catch (e) {
            console.log("Error re-checking invoice", e);
            return false;
        }
    };

    // ── Render helpers ──
    const renderSubheading = () => {
        if (isCancelled) return `Cancelled${cancelledDate ? ` ${cancelledDate}` : ''}`;
        if (isPaid) return `Paid${paidDate ? ` ${paidDate}` : ''}`;
        return sentDate ? `Sent ${sentDate}` : null;
    };

    const renderPaymentMethods = () => {
        if (isCancelled) {
            return (
                <div className="notice-banner">
                    <i className="fas fa-ban"></i>
                    <span>This invoice was cancelled and no payment is due.</span>
                </div>
            );
        }
        if (isPaid) {
            return (
                <div className="notice-banner">
                    <i className="fas fa-circle-check"></i>
                    <span>This invoice has been paid in full.</span>
                </div>
            );
        }
        if (isAwaitingConfirmation) {
            return (
                <div className="notice-banner" role="status">
                    <i className="fas fa-circle-check"></i>
                    <span>Marked as paid</span>
                </div>
            );
        }
        // Back from Stripe, but the webhook may not have updated the invoice
        // yet. Don't offer payment again in the meantime.
        if (returnedFromCheckout) {
            return (
                <div className="notice-banner" role="status">
                    <i className="fas fa-circle-check"></i>
                    <span>
                        Payment complete.{' '}
                        <span className="notice-sub">This invoice will show as paid shortly.</span>
                    </span>
                </div>
            );
        }
        if (checkoutUrl) {
            return (
                <div className="checkout-panel" role="status">
                    <div className="checkout-icon"><i className="fas fa-lock"></i></div>
                    <h2 className="checkout-title">A checkout window should open</h2>
                    <p className="checkout-text">
                        Finish paying {money(amountOwed)} on the secure Stripe page.
                        If it didn't open, use the button below.
                    </p>
                    <div className="checkout-actions">
                        <a
                            href={checkoutUrl}
                            className="wizard-btn primary"
                        >
                            <span>Open checkout</span>
                            <i className="fas fa-arrow-right"></i>
                        </a>
                        <button
                            type="button"
                            className="text-btn"
                            onClick={() => setCheckoutUrl(null)}
                        >
                            Choose a different payment method
                        </button>
                    </div>
                </div>
            );
        }
        if (accepted.length === 0) {
            return <p className="muted-text">No payment methods are set up for this invoice yet.</p>;
        }
        return (
            <>
                <span className="section-label">Choose how to pay</span>
                <div className="method-list" role="radiogroup" aria-label="Payment method">
                    {accepted.map(key => {
                        const m = PAYMENT_METHODS[key];
                        const active = paymentMethod === key;
                        const details = renderMethodDetails(key);
                        return (
                            <div key={key} className={`method-item ${active ? 'active' : ''}`}>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={active}
                                className={`method-card ${active ? 'active' : ''}`}
                                onClick={() => selectMethod(key)}
                            >
                                <i className={`${m.icon} method-icon`}></i>
                                <span className="method-text">
                                    <span className="method-label">{m.label}</span>
                                    {m.sub && <span className="method-sub">{m.sub}</span>}
                                </span>
                                {preferred.includes(key) && <span className="method-tag">Preferred</span>}
                            </button>
                            {/* Always mounted so it can animate open and closed */}
                            {details && (
                                <div className={`method-detail-wrap ${active ? 'open' : ''}`} aria-hidden={!active}>
                                    <div className="method-detail-inner">{details}</div>
                                </div>
                            )}
                            </div>
                        );
                    })}
                </div>

                <button
                    className={`wizard-btn primary ${payDisabled ? 'disabled' : ''}`}
                    onClick={handlePay}
                    disabled={payDisabled || submitting}
                    aria-busy={submitting}
                >
                    {submitting ? (
                        <>
                            <div className="btn-spinner"></div>
                            <span>{paymentMethod === 'card' ? 'Opening secure checkout' : 'Marking as paid...'}</span>
                        </>
                    ) : (
                        <>
                            <span>{payLabel}</span>
                            {!payDisabled && <i className="fas fa-arrow-right"></i>}
                        </>
                    )}
                </button>
                {submitError && <p className="pay-error" role="alert">{submitError}</p>}
            </>
        );
    };

    const renderInvoice = () => (
        <div className="inv-layout step-content">
            {/* Top bar: business + client avatars, overlapping */}
            <div className="inv-topbar">
                <div className="avatar-pair" ref={brandRef}>
                    <Avatar src={serverAvatar} name={businessName} fallbackIcon="fas fa-store" className="avatar-server" />
                    <Avatar src={clientAvatar} name={customerName} fallbackIcon="fas fa-user" className="avatar-client" />
                </div>
            </div>

            <div className="inv-grid">
                {/* Left column */}
                <div className="inv-main">
                    <h1 className="inv-headline">
                        {/* Partially paid: lead with what's still owed, matching
                            the "Amount due" line in the summary. */}
                        {amountPaid > 0 && !isPaid
                            ? (businessName
                                ? `${businessName} has sent you an invoice with ${money(amountOwed)} due`
                                : `You have ${money(amountOwed)} due on this invoice`)
                            : (businessName
                                ? `${businessName} has sent you an invoice for ${money(total)}`
                                : `You've received an invoice for ${money(total)}`)}
                    </h1>
                    <div className="inv-subrow">
                        <span className={`status-pill ${statusKey}`}>{STATUS_LABEL[statusKey] || statusKey}</span>
                        {renderSubheading() && <span className="inv-due">{renderSubheading()}</span>}
                    </div>

                    <hr className="inv-divider" />

                    <div className="no-print">{renderPaymentMethods()}</div>
                </div>

                {/* Right column */}
                <aside className="inv-side">
                    {(customerName || customerEmail || customerPhone) && (
                        <div className="side-card">
                            <h2 className="side-title">Customer</h2>
                            {customerName && <p className="side-line">{customerName}</p>}
                            {customerEmail && <p className="side-line">{customerEmail}</p>}
                            {customerPhone && <p className="side-line">{customerPhone}</p>}
                        </div>
                    )}

                    <div className="side-card">
                        <h2 className="side-title">Summary</h2>
                        {sentDate && (
                            <div className="side-meta">
                                <p>{sentDate}</p>
                            </div>
                        )}

                        <div className="item-list">
                            {lineItems.map(it => (
                                <div key={it.key} className="item">
                                    <div className="item-text">
                                        <span className="item-name">{it.name}</span>
                                        <span className="item-sub">
                                            {it.qty} × {money(it.unit || it.lineTotal / it.qty)}
                                        </span>
                                        {it.note && <span className="item-note">{it.note}</span>}
                                    </div>
                                    <span className="row-value">{money(it.lineTotal)}</span>
                                </div>
                            ))}
                        </div>

                        <div className="row">
                            <span className="row-label">Subtotal</span>
                            <span className="row-value">{money(subtotal)}</span>
                        </div>
                        {adjustments !== 0 && (
                            <div className="row">
                                <span className="row-label">{adjustments > 0 ? 'Tax & fees' : 'Discount'}</span>
                                <span className="row-value">{adjustments < 0 ? '−' : ''}{money(Math.abs(adjustments))}</span>
                            </div>
                        )}
                        {/* Partially paid: "Amount due" is the figure that
                            matters, so it takes the bold total slot and the
                            Total row is dropped. */}
                        {amountPaid > 0 && !isPaid ? (
                            <>
                                <div className="row">
                                    <span className="row-label">Paid</span>
                                    <span className="row-value">−{money(amountPaid)}</span>
                                </div>
                                <div className="row row-total">
                                    <span>Amount due</span>
                                    <span>{money(amountOwed)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="row row-total">
                                <span>Total</span>
                                <span>{money(total)}</span>
                            </div>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );

    return (
        <div className="sp-root">
            <Helmet>
                <title>{businessName ? `Invoice from ${businessName}` : 'Invoice'} | Verca</title>
                <meta property="og:title" content={`${businessName ? `Invoice from ${businessName}` : 'Invoice'} | Verca`} />
                <meta property="og:url" content={`https://verca.io/invoice/${id || ''}`} />
            </Helmet>

            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap" rel="stylesheet" />

            {/* Nav */}
            {/* Nav — the wordmark hands off to the invoice branding on scroll */}
            <nav className={`sp-nav no-print${navBranded ? ' branded' : ''}`}>
                <div className="sp-nav-inner">
                    <div
                        className="sp-nav-slot sp-logo"
                        onClick={() => navigate('/')}
                        role="button"
                        tabIndex={navBranded ? -1 : 0}
                        aria-hidden={navBranded}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate('/');
                            }
                        }}
                    >
                        <span className="sp-logo-text">Verca</span>
                    </div>

                    {invoice && (
                        <button
                            type="button"
                            className="sp-nav-slot sp-nav-brand"
                            onClick={scrollToTop}
                            tabIndex={navBranded ? 0 : -1}
                            aria-hidden={!navBranded}
                            aria-label={`${businessName || 'Invoice'} — back to top`}
                        >
                            <span className="avatar-pair" aria-hidden="true">
                                <Avatar src={serverAvatar} name={businessName} fallbackIcon="fas fa-store" className="avatar-server avatar-sm" />
                                <Avatar src={clientAvatar} name={customerName} fallbackIcon="fas fa-user" className="avatar-client avatar-sm" />
                            </span>
                            <span className="sp-nav-brand-name">{businessName || 'Invoice'}</span>
                        </button>
                    )}
                </div>
            </nav>

            <main className="sp-main">
                <div className="sp-container">
                    {loading ? (
                        <div className="sp-loading">
                            <div className="sp-spinner"></div>
                            <p>Loading invoice...</p>
                        </div>
                    ) : invoice ? (
                        renderInvoice()
                    ) : (
                        <div className="sp-error">
                            <div className="sp-error-icon"><i className="fas fa-file-invoice"></i></div>
                            <h2>Invoice Not Found</h2>
                            <p>Unable to locate invoice</p>
                            <button className="wizard-btn secondary" onClick={() => navigate('/')}>Back to Home</button>
                        </div>
                    )}
                </div>
            </main>

            <style>{`
                /* ── Reset & Base ── */
                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                /* Monochrome palette (matches ServicesPage) */
                .sp-root {
                    min-height: 100vh;
                    background: #FFFFFF;
                    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
                    color: #000000;
                    -webkit-font-smoothing: antialiased;
                }

                /* ── Nav ── */
                .sp-nav {
                    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
                    padding: 1.25rem 1.5rem;
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(16px);
                    border-bottom: 1px solid #EDEDED;
                }
                /* The wordmark and the invoice branding share one slot on the
                   left and crossfade past each other: the wordmark lifts away as
                   the branding rises into place. Both are absolutely positioned,
                   so the nav height never shifts. (Mirrors OfferingsPage.) */
                .sp-nav-inner {
                    position: relative;
                    max-width: 1040px; margin: 0 auto;
                    min-height: 34px;
                }
                .sp-nav-slot {
                    position: absolute; left: 0; top: 50%;
                    display: flex; align-items: center; gap: 10px;
                    max-width: 100%; min-width: 0;
                    padding: 0; background: transparent; border: none;
                    font-family: 'DM Sans', sans-serif;
                    text-align: left; cursor: pointer;
                    transition: opacity 280ms cubic-bezier(0.32, 0, 0.24, 1),
                                transform 280ms cubic-bezier(0.32, 0, 0.24, 1);
                }
                .sp-nav-slot:focus-visible { outline: 2px solid #000000; outline-offset: 6px; border-radius: 6px; }

                /* Wordmark: visible at the top of the page, lifts out on scroll. */
                .sp-logo { transform: translateY(-50%); opacity: 1; }
                .sp-logo:hover { opacity: 0.65; }
                .sp-logo-text { font-size: 1.35rem; font-weight: 700; letter-spacing: -0.5px; color: #000000; }
                .sp-nav.branded .sp-logo {
                    opacity: 0;
                    transform: translateY(calc(-50% - 10px));
                    pointer-events: none;
                }

                /* Invoice branding: waits just below, rises in as you scroll. */
                .sp-nav-brand {
                    opacity: 0;
                    transform: translateY(calc(-50% + 10px));
                    pointer-events: none;
                }
                .sp-nav.branded .sp-nav-brand {
                    opacity: 1;
                    transform: translateY(-50%);
                    pointer-events: auto;
                }
                .sp-nav-brand:hover { opacity: 0.65; }
                .sp-nav-brand-name {
                    min-width: 0;
                    font-size: 1.15rem; font-weight: 700; color: #000000;
                    letter-spacing: -0.5px; line-height: 1.2;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }

                /* ── Main ── (wider than ServicesPage for the two-column layout) */
                .sp-main { padding-top: 110px; min-height: 100vh; background: #FFFFFF; }
                .sp-container { max-width: 1040px; margin: 0 auto; padding: 0 1.5rem 4rem; }

                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .step-content { animation: fadeSlideIn 0.35s cubic-bezier(0.32, 0, 0.24, 1); }

                @media (prefers-reduced-motion: reduce) {
                    .step-content, .sp-error { animation: none; }
                    .method-detail-wrap, .method-detail-inner { transition: none; }
                    /* Keep the branding swap, drop the travel: opacity only. */
                    .sp-nav-slot { transition: opacity 1ms linear; }
                    .sp-nav.branded .sp-logo,
                    .sp-nav-brand { transform: translateY(-50%); }
                    .wizard-btn.primary:hover:not(.disabled) { transform: none; }
                }

                /* ── Loading ── */
                .sp-loading {
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; min-height: 50vh; gap: 1.25rem;
                }
                .sp-loading p { color: #767676; font-size: 0.95rem; }
                .sp-spinner {
                    width: 40px; height: 40px;
                    border: 2px solid #E6E6E6;
                    border-top-color: #000000; border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ── Error ── */
                .sp-error {
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; min-height: 50vh; text-align: center;
                    max-width: 420px; margin: 0 auto;
                    animation: fadeSlideIn 0.4s ease;
                }
                .sp-error-icon {
                    width: 72px; height: 72px; border-radius: 20px;
                    background: #F6F6F6;
                    display: flex; align-items: center; justify-content: center; margin-bottom: 1.5rem;
                }
                .sp-error-icon i { font-size: 1.75rem; color: #A3A3A3; }
                .sp-error h2 { font-size: 1.75rem; font-weight: 700; margin-bottom: 0.5rem; letter-spacing: -0.5px; }
                .sp-error p { color: #767676; margin-bottom: 2rem; }

                /* ── Invoice layout ── */
                .inv-topbar {
                    display: flex; align-items: flex-start; justify-content: space-between;
                    margin-bottom: 40px;
                }
                /* ── Avatars ── two circles overlapping, client tucked behind
                   the business with a white ring so they read as one mark. */
                .avatar-pair { display: flex; align-items: center; }
                .avatar {
                    --avatar-size: 76px;
                    width: var(--avatar-size); height: var(--avatar-size);
                    border-radius: 50%; overflow: hidden; flex-shrink: 0;
                    display: flex; align-items: center; justify-content: center;
                    background: #F2F2F2; color: #000000;
                    box-shadow: 0 0 0 1.5px #FFFFFF, 0 2px 8px rgba(0, 0, 0, 0.08);
                    position: relative;
                }
                .avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .avatar i { font-size: 1.55rem; }
                .avatar-initials { font-size: 1.4rem; font-weight: 700; letter-spacing: 0.5px; }
                .avatar-server { z-index: 2; background: #000000; color: #FFFFFF; }
                .avatar-client { z-index: 1; margin-left: calc(var(--avatar-size) * -0.2); }

                /* Miniature pair in the nav, same overlap at nav scale */
                .avatar.avatar-sm { --avatar-size: 34px; box-shadow: 0 0 0 1.5px #FFFFFF; }
                .avatar.avatar-sm i { font-size: 13px; }
                .avatar.avatar-sm .avatar-initials { font-size: 12px; letter-spacing: 0; }

                .inv-grid { display: grid; grid-template-columns: 1fr; gap: 32px; }
                @media (min-width: 900px) {
                    .inv-grid { grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr); gap: 56px; }
                    .inv-side { position: sticky; top: 110px; align-self: start; }
                }

                .inv-headline {
                    font-size: 2.5rem; font-weight: 700; color: #000000;
                    letter-spacing: -1px; line-height: 1.12; margin-bottom: 16px;
                }
                .inv-subrow { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
                .inv-due { font-size: 1rem; color: #767676; font-weight: 500; }
                .inv-divider { border: none; border-top: 1px solid #EDEDED; margin: 28px 0; }

                .status-pill {
                    display: inline-block;
                    font-size: 12px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase;
                    padding: 5px 12px; border-radius: 8px;
                    background: #F6F6F6; color: #444444; border: 1px solid #E6E6E6;
                }
                .status-pill.paid, .status-pill.awaiting { background: #000000; color: #FFFFFF; border-color: #000000; }
                .status-pill.cancelled { color: #A3A3A3; text-decoration: line-through; }

                .section-label {
                    font-size: 14px; font-weight: 600; color: #767676;
                    margin-bottom: 12px; display: block;
                }
                .muted-text { color: #767676; font-size: 15px; }

                /* ── Payment method cards ── */
                .method-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
                /* The outer item owns the border so the card and its expanding
                   details always read as one block. */
                .method-item {
                    border: 1px solid #E0E0E0; border-radius: 14px; overflow: hidden;
                    background: #FFFFFF;
                    transition: border-color 0.3s var(--ease-out), box-shadow 0.3s var(--ease-out);
                }
                .method-item:hover:not(.active) { border-color: #C4C4C4; }
                .method-item.active { border-color: #000000; box-shadow: 0 0 0 1px #000000; }
                .method-card {
                    display: flex; align-items: center; gap: 18px; width: 100%;
                    background: transparent; border: none; border-radius: 0;
                    padding: 22px 24px; text-align: left; cursor: pointer;
                    font-family: 'DM Sans', sans-serif; color: #000000;
                    transition: background 0.15s;
                }
                .method-item:not(.active) .method-card:hover { background: #FAFAFA; }
                .method-card:focus-visible { outline: 2px solid #000000; outline-offset: -4px; border-radius: 12px; }
                .method-icon { width: 24px; font-size: 1.2rem; text-align: center; flex-shrink: 0; }
                .method-text { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
                .method-label { font-size: 17px; font-weight: 600; }
                .method-sub { font-size: 14px; color: #444444; }
                .method-tag {
                    font-size: 11px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase;
                    color: #444444; background: #F6F6F6; border-radius: 8px; padding: 4px 10px;
                    flex-shrink: 0;
                }
                .method-check { font-size: 14px; flex-shrink: 0; }

                /* Smooth expand: animating grid rows 0fr → 1fr lets the panel
                   grow to its natural height without measuring it. */
                /* Ease-out-quint: starts quickly, then settles gently, so the
                   panel feels responsive without an abrupt stop. */
                .sp-root {
                    --ease-out: cubic-bezier(0.22, 1, 0.36, 1);
                    --ease-in-out: cubic-bezier(0.65, 0, 0.35, 1);
                }
                .method-detail-wrap {
                    display: grid; grid-template-rows: 0fr;
                    /* closing: a touch quicker, symmetric ease */
                    transition: grid-template-rows 0.36s var(--ease-in-out);
                }
                .method-detail-wrap.open {
                    grid-template-rows: 1fr;
                    transition: grid-template-rows 0.5s var(--ease-out);
                }
                .method-detail-inner {
                    min-height: 0; overflow: hidden;
                    opacity: 0; transform: translateY(-8px);
                    /* hidden content stays out of the tab order once collapsed */
                    visibility: hidden;
                    transition:
                        opacity 0.18s ease-out,
                        transform 0.3s var(--ease-in-out),
                        visibility 0s linear 0.36s;
                }
                .method-detail-wrap.open .method-detail-inner {
                    opacity: 1; transform: translateY(0);
                    visibility: visible;
                    /* content fades in just after the panel starts opening */
                    transition:
                        opacity 0.4s var(--ease-out) 0.1s,
                        transform 0.5s var(--ease-out) 0.06s,
                        visibility 0s;
                }
                .method-detail { padding: 4px 24px 22px 66px; }
                .detail-message { font-size: 15px; color: #767676; line-height: 1.55; margin-bottom: 16px; }
                .detail-message:last-child { margin-bottom: 0; }
                .detail-message strong { color: #000000; font-weight: 600; }

                .pay-link {
                    display: flex; align-items: center; justify-content: center; gap: 10px;
                    width: 100%; padding: 15px 20px; border-radius: 12px;
                    text-decoration: none; font-size: 16px; font-weight: 600;
                    transition: background 0.2s, transform 0.2s;
                }
                .pay-link:hover { transform: translateY(-1px); }
                .pay-link.venmo { background: #008CFF; color: #FFFFFF; }
                .pay-link.venmo:hover { background: #0072D6; }
                .pay-link.venmo:focus-visible { outline: 3px solid rgba(0, 140, 255, 0.32); outline-offset: 2px; }
                /* White on Cash App green fails contrast, so it uses dark ink */
                .pay-link.cashapp { background: #00D632; color: #0A2412; }
                .pay-link.cashapp:hover { background: #00BE2C; }
                .pay-link.cashapp:focus-visible { outline: 3px solid rgba(0, 214, 50, 0.32); outline-offset: 2px; }

                .or-divider {
                    display: flex; align-items: center; gap: 14px;
                    margin: 18px 0 14px;
                    color: #A3A3A3; font-size: 12px; font-weight: 600;
                    letter-spacing: 1.4px; text-transform: uppercase;
                }
                .or-divider::before, .or-divider::after {
                    content: ''; flex: 1; height: 1px; background: #E6E6E6;
                }
                .address-label { font-size: 14px; color: #767676; margin-bottom: 10px; }

                .copy-field {
                    display: flex; align-items: center; justify-content: space-between; gap: 12px;
                    width: 100%; padding: 14px 16px;
                    background: #FFFFFF; border: 1px solid #D6D6D6; border-radius: 12px;
                    font-family: 'DM Sans', sans-serif; color: #000000; cursor: pointer;
                    text-align: left; transition: background 0.2s, border-color 0.2s;
                }
                .copy-field:hover { background: #FAFAFA; border-color: #A9AEB5; }
                .copy-field:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }
                .copy-text { font-size: 16px; font-weight: 500; word-break: break-all; flex: 1; }
                .copy-action {
                    display: flex; align-items: center; gap: 6px; flex-shrink: 0;
                    font-size: 12px; font-weight: 500; color: #767676;
                }

                .notice-banner {
                    display: flex; align-items: center; gap: 12px;
                    background: #F6F6F6; border: 1px solid #EDEDED; border-radius: 14px;
                    padding: 20px 22px; font-size: 16px; font-weight: 600;
                    animation: fadeSlideIn 0.4s cubic-bezier(0.22, 1, 0.36, 1);
                }
                .notice-banner > i { flex-shrink: 0; }
                .notice-sub { font-weight: 400; color: #767676; }

                .btn-spinner {
                    width: 20px; height: 20px;
                    border: 2px solid rgba(255,255,255,0.25); border-radius: 50%;
                    border-top-color: #FFFFFF; animation: spin 0.7s linear infinite;
                }
                .pay-error { font-size: 14px; color: #CC0023; margin-top: 12px; text-align: center; }

                /* ── Card checkout hand-off ── */
                .checkout-panel {
                    display: flex; flex-direction: column; align-items: center; text-align: center;
                    background: #F6F6F6; border: 1px solid #EDEDED; border-radius: 14px;
                    padding: 32px 24px;
                    animation: fadeSlideIn 0.4s cubic-bezier(0.22, 1, 0.36, 1);
                }
                .checkout-icon {
                    width: 56px; height: 56px; border-radius: 50%;
                    background: #FFFFFF; border: 1px solid #E6E6E6;
                    display: flex; align-items: center; justify-content: center;
                    margin-bottom: 16px;
                }
                .checkout-icon i { font-size: 1.2rem; color: #000000; }
                .checkout-title { font-size: 1.2rem; font-weight: 700; letter-spacing: -0.3px; margin-bottom: 8px; }
                .checkout-text { font-size: 15px; color: #767676; line-height: 1.55; max-width: 360px; }
                .checkout-actions {
                    display: flex; flex-direction: column; align-items: center; gap: 12px;
                    width: 100%; margin-top: 22px;
                }
                .checkout-actions .wizard-btn { text-decoration: none; }
                .text-btn {
                    background: transparent; border: none; padding: 6px 4px;
                    font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 500;
                    color: #767676; text-decoration: underline; text-underline-offset: 3px;
                    cursor: pointer; transition: color 0.2s;
                }
                .text-btn:hover { color: #000000; }
                .text-btn:focus-visible { outline: 2px solid #000000; outline-offset: 2px; border-radius: 4px; }

                /* ── Side cards ── */
                .inv-side { display: flex; flex-direction: column; gap: 20px; }
                .side-card {
                    background: #FFFFFF; border: 1px solid #E0E0E0; border-radius: 14px;
                    padding: 28px;
                }
                .side-title { font-size: 1.25rem; font-weight: 700; letter-spacing: -0.3px; margin-bottom: 14px; }
                .side-line { font-size: 15px; color: #767676; line-height: 1.7; overflow-wrap: anywhere; }
                .side-meta { font-size: 14px; color: #767676; line-height: 1.6; margin-bottom: 20px; }

                /* ── Line items ── */
                .item-list {
                    display: flex; flex-direction: column; gap: 14px;
                    padding-bottom: 18px; margin-bottom: 6px; border-bottom: 1px solid #EDEDED;
                }
                .item { display: flex; align-items: center; gap: 14px; }
                .item-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
                .item-name { font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
                .item-sub { font-size: 13px; color: #767676; }
                .item-note { font-size: 13px; color: #767676; font-style: italic; }

                .row {
                    display: flex; align-items: baseline; justify-content: space-between; gap: 16px;
                    padding: 12px 0;
                }
                .row-label { font-size: 15px; font-weight: 600; }
                .row-value { font-size: 15px; font-variant-numeric: tabular-nums; flex-shrink: 0; }
                .row-total {
                    margin-top: 8px; padding-top: 18px; border-top: 1px solid #EDEDED;
                    font-size: 1.4rem; font-weight: 700;
                }
                .row-total span:last-child, .row-owed span:last-child { font-variant-numeric: tabular-nums; }
                .row-owed { font-size: 1.1rem; font-weight: 700; }

                /* ── Buttons ── */
                .wizard-btn {
                    display: flex; align-items: center; justify-content: center; gap: 10px;
                    width: 100%; padding: 18px 24px; border-radius: 18px;
                    font-size: 16px; font-weight: 600; font-family: 'DM Sans', sans-serif;
                    border: none; cursor: pointer; transition: all 0.25s; letter-spacing: 0.3px;
                }
                .wizard-btn:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }
                .wizard-btn.primary { background: #000000; color: #FFFFFF; }
                .wizard-btn.primary:hover:not(.disabled) { background: #1F1F1F; transform: translateY(-2px); }
                .wizard-btn.primary.disabled {
                    background: #E6E6E6; color: #9A9A9A; cursor: not-allowed; transform: none;
                }
                .wizard-btn.secondary { background: #FFFFFF; color: #000000; border: 1px solid #D6D6D6; }
                .wizard-btn.secondary:hover { background: #F6F6F6; border-color: #B8B8B8; }

                /* ── Responsive ── */
                @media (max-width: 640px) {
                    .sp-container { padding: 0 1.25rem 3rem; }
                    .inv-headline { font-size: 1.85rem; }
                    .avatar { --avatar-size: 62px; }
                    .avatar.avatar-sm { --avatar-size: 30px; }
                    .sp-nav-brand-name { font-size: 1.05rem; letter-spacing: -0.4px; }
                    .side-card { padding: 22px; }
                    .method-card { padding: 18px 20px; gap: 14px; }
                    .method-detail { padding: 4px 20px 20px; }
                }

                /* ── Print (Download PDF) ── */
                @media print {
                    .no-print { display: none !important; }
                    .sp-main { padding-top: 0; }
                    .inv-grid { grid-template-columns: 1fr 1fr; }
                    .step-content { animation: none; }
                }
            `}</style>
        </div>
    );
};

export default InvoicesPage;